import { faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { useApi } from 'hooks/useApi';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    clubSlug: string;
    moduleKey: string;
    moduleTitle: string;
    /** Current allowlist. null means nothing is hidden, so the banner never renders then. */
    visible: string[] | null | undefined;
    onRevealed: () => void;
    onShowAllForSession: () => void;
}

/**
 * Shown when someone deep-links into a module the club has hidden.
 *
 * Hiding is not deactivating: the route stays reachable, the API stays open and the
 * data stays put. Without this banner the module would look broken and produce the
 * "my invoices are gone" support case, which is exactly what the setting is meant to
 * avoid. Two ways out — put it back for everyone (needs club.settings), or reveal
 * everything for this session only.
 */
export const HiddenModuleBanner: React.FunctionComponent<Props> = ({
    clubSlug,
    moduleKey,
    moduleTitle,
    visible,
    onRevealed,
    onShowAllForSession
}) => {
    const api = useApi();
    const { t } = useTranslation('general');
    const [saving, setSaving] = useState(false);

    // Adds this one module back to the allowlist — the "two clicks" the spec asks for.
    const reveal = async () => {
        if (!Array.isArray(visible)) return;

        setSaving(true);
        try {
            await api({
                url: `club/${clubSlug}/modules`,
                method: 'PUT',
                data: { visible: [...visible, moduleKey] }
            });
            onRevealed();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-warning tw-bg-warningLight tw-px-4 tw-py-3">
            <FontAwesomeIcon icon={faEyeSlash} className="tw-text-warning" />
            <span className="tw-flex-1 tw-text-sm tw-text-accents-8">
                {t('modules.hiddenBanner', { module: moduleTitle })}
            </span>
            <Button size="sm" color="light" onPress={onShowAllForSession}>
                {t('modules.showForSession')}
            </Button>
            <Button size="sm" color="success" isDisabled={saving} onPress={reveal}>
                {t('modules.reveal')}
            </Button>
        </div>
    );
};

export default HiddenModuleBanner;
